import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  useTheme,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  alpha,
  Stack,
  styled,
} from "@mui/material";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Styled Paper for the form
const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4, 3),
  borderRadius: theme.shape.borderRadius * 2,
  background: `linear-gradient(145deg, ${
    theme.palette.background.paper
  } 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
  boxShadow: `0 12px 30px -10px ${alpha(theme.palette.primary.main, 0.25)}`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

export const HeroSection = () => {
  const theme = useTheme();
  const [formData, setFormData] = React.useState({
    nome: "",
    telefone: "",
    email: "",
    tipoPlano: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name as string]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("Dados do formulário:", formData);
    // Lógica para enviar os dados
  };

  return (
    <Box
      sx={{
        pt: { xs: 8, md: 10 },
        pb: { xs: 10, md: 12 },
        backgroundColor: theme.palette.background.default,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Decorative background elements */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "-20%",
            left: "-15%",
            width: { xs: 300, md: 500 },
            height: { xs: 300, md: 500 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(
              theme.palette.primary.light,
              0.15
            )} 0%, transparent 60%)`,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-25%",
            right: "-20%",
            width: { xs: 250, md: 400 },
            height: { xs: 250, md: 400 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(
              theme.palette.secondary.light,
              0.1
            )} 0%, transparent 60%)`,
          }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          {/* Text Column */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
                lineHeight: 1.3,
                mb: 2,
                color: theme.palette.primary.dark,
              }}
            >
              A tranquilidade de ter o plano de saúde certo para você
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{
                mb: 4,
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              Comparamos as melhores opções do mercado para você economizar
              tempo e dinheiro. Simples, rápido e sem complicação.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                href="#planos"
              >
                Ver Planos
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                href="#formulario"
              >
                Simular Cotação
              </Button>
            </Stack>
          </Grid>

          {/* Form Column */}
          <Grid item xs={12} md={6} id="formulario">
            <FormPaper elevation={0}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}
              >
                <DescriptionOutlinedIcon color="primary" />
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{ fontWeight: 600, color: theme.palette.primary.dark }}
                >
                  Simule sua Cotação
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ mb: 3, color: theme.palette.text.secondary }}
              >
                É rápido, fácil e gratuito. Preencha abaixo para começar!
              </Typography>

              <Box component="form" noValidate onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Seu Nome"
                  variant="filled"
                  margin="normal"
                  required
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  sx={{ mb: 2 }}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <AssignmentIndOutlinedIcon
                        sx={{ mr: 1, color: theme.palette.action.active }}
                      />
                    ),
                    sx: {
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                  InputLabelProps={{
                    sx: { color: theme.palette.text.secondary },
                  }}
                />
                <TextField
                  fullWidth
                  label="Seu Telefone"
                  variant="filled"
                  margin="normal"
                  required
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  type="tel"
                  sx={{ mb: 2 }}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <PhoneAndroidIcon
                        sx={{ mr: 1, color: theme.palette.action.active }}
                      />
                    ),
                    sx: {
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                  InputLabelProps={{
                    sx: { color: theme.palette.text.secondary },
                  }}
                />
                <TextField
                  fullWidth
                  label="Seu E-mail"
                  variant="filled"
                  margin="normal"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  type="email"
                  sx={{ mb: 2 }}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <MailOutlineIcon
                        sx={{ mr: 1, color: theme.palette.action.active }}
                      />
                    ),
                    sx: {
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                  InputLabelProps={{
                    sx: { color: theme.palette.text.secondary },
                  }}
                />
                <FormControl
                  fullWidth
                  margin="normal"
                  variant="filled"
                  sx={{ mb: 3 }}
                >
                  <InputLabel
                    id="tipo-plano-label"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    Qual tipo de plano busca?
                  </InputLabel>
                  <Select
                    labelId="tipo-plano-label"
                    id="tipo-plano"
                    name="tipoPlano"
                    value={formData.tipoPlano}
                    label="Qual tipo de plano busca?"
                    onChange={(e) =>
                      handleChange(e as React.ChangeEvent<HTMLInputElement>)
                    }
                    disableUnderline
                    sx={{
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    }}
                  >
                    <MenuItem value="individual">Individual</MenuItem>
                    <MenuItem value="familiar">Familiar</MenuItem>
                    <MenuItem value="empresarial">Empresarial</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  type="submit"
                  color="secondary"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                  }}
                >
                  Receber Simulação Grátis
                </Button>
              </Box>
            </FormPaper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
